import { Plus, Cpu, Play, Pencil, Trash2, Zap, ZapOff, Copy, LogIn } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { TID } from "../../lib/testIds";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

export default function EngineList({
  engines, adapters, loading, onAdd, onEdit, onRun, onDeleted, onDuplicate, onLogin,
}) {
  const adapterById = Object.fromEntries((adapters || []).map((a) => [a.id, a]));

  const remove = async (id) => {
    if (!window.confirm(`¿Eliminar el motor '${id}'?`)) return;
    try {
      await api.delete(`/engine-registry/${id}`);
      toast.success("Motor eliminado");
      onDeleted?.();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-400">
          {engines.length} motor{engines.length === 1 ? "" : "es"} registrado{engines.length === 1 ? "" : "s"}
        </div>
        <Button
          data-testid={TID.engines.addButton}
          onClick={onAdd}
          size="sm"
          className="gap-2 bg-white text-black hover:bg-zinc-200"
        >
          <Plus className="h-3.5 w-3.5" /> Nuevo motor
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {loading ? (
          <div className="text-xs text-zinc-500">Cargando…</div>
        ) : engines.length === 0 ? (
          <div className="col-span-2 rounded-md border border-dashed border-zinc-800 p-8 text-center text-xs text-zinc-500">
            Aún no hay motores registrados.
          </div>
        ) : engines.map((e) => (
          <div
            key={e.id}
            data-testid={TID.engines.card(e.id)}
            className="rounded-md border border-zinc-800 bg-zinc-950 p-4 space-y-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Cpu className="h-3.5 w-3.5 text-zinc-500" />
                  <span className="truncate">{e.name}</span>
                </div>
                <div className="text-[11px] text-zinc-500 font-mono truncate">
                  {e.id} · {e.provider} · {e.adapter}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {e.adapter === "chatgpt_web" && <>
                  <button aria-label="Duplicar configuración de ChatGPT" title="Duplicar para otra cuenta" className="p-1.5 text-zinc-400 hover:text-white rounded" onClick={() => onDuplicate(e)}><Copy className="h-3.5 w-3.5" /></button>
                  {e.config?.profile !== "chatgpt-kiko" && <button aria-label="Iniciar sesión en ChatGPT" title="Iniciar sesión en esta cuenta" className="p-1.5 text-zinc-400 hover:text-emerald-400 rounded" onClick={() => onLogin(e)}><LogIn className="h-3.5 w-3.5" /></button>}
                </>}
                <button
                  data-testid={TID.engines.test(e.id)}
                  onClick={() => onRun(e)}
                  aria-label={e.active ? "Probar motor" : "Probar motor inactivo (sin activarlo)"}
                  title={e.active ? "Probar motor" : "Probar motor inactivo (sin activarlo)"}
                  className="p-1.5 text-zinc-400 hover:text-emerald-400 rounded"
                >
                  <Play className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={TID.engines.edit(e.id)}
                  onClick={() => onEdit(e)}
                  className="p-1.5 text-zinc-400 hover:text-white rounded"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={TID.engines.delete(e.id)}
                  onClick={() => remove(e.id)}
                  className="p-1.5 text-zinc-400 hover:text-red-400 rounded"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {e.adapter === "chatgpt_web" && <div className="flex items-center justify-between gap-2 text-xs text-zinc-400">
              <span>Perfil: {e.config?.profile || "Sin configurar"}</span>
              {e.config?.profile !== "chatgpt-kiko" && <Button size="sm" variant="outline" onClick={() => onLogin(e)}>Iniciar sesión</Button>}
            </div>}
            {adapterById[e.adapter]?.description && (
              <div className="text-[11px] text-zinc-500 leading-relaxed">
                {adapterById[e.adapter].description}
              </div>
            )}

            {e.capabilities?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {e.capabilities.map((c) => (
                  <Badge key={c} variant="outline" className="text-[10px] border-zinc-700 text-zinc-400">
                    {c}
                  </Badge>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <span className={
                "inline-flex items-center gap-1.5 text-[11px] " +
                (e.active ? "text-emerald-400" : "text-zinc-500")
              }>
                {e.active ? <Zap className="h-3 w-3" /> : <ZapOff className="h-3 w-3" />}
                {e.active ? "Activo" : "Inactivo"}
              </span>
              <span className="text-[10px] text-zinc-600 font-mono">
                {new Date(e.updated_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
